import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router";
import { getState, pushWithModifier } from "../../helper/page.helper";
import { authSelector, userInfoSelector } from "../../redux/selectors/auth.selector";
import "./F003.scss";
import { FCStaffForListSchema, GeneralForListSchema } from "../../generated";
import { fetchGeneralsForList, fetchStaffById } from "../../services/api.service";
import { F001Url, F004Url, getForHistoryPush } from "../../helper/url.helper";
import { HEADQUARTER_USER } from "../../constants.js";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import CustomButton, { CB_CLASS, CB_ALIGN } from "../../components/Button/CustomButton";
import Title from "../../components/Title/Title";
import PaginationTable from "../../components/PaginationTable/PaginationTable";
import FacilityName from "../../components/FacilityName/FacilityName";
import Pict, { SIZES } from "../../components/Pict/User";
import Label from "../../components/Label/Label";
import { useLoadingIndicator } from "../../hooks/LoadingIndicator.hooks";
import { useAuth } from "../../helper/auth.helper";
import { notifyError } from "../../helper/settings.helper";

interface iF003 {
  fc_id: number;
}

const F003 = () => {
  const { checkLoggedIn } = useAuth();
  const maxWidth = 900;
  const authState = useSelector(authSelector);
  const location = useLocation<iF003>();
  const state = getState(location);
  const history = useHistory();
  const userInfo = useSelector(userInfoSelector);
  const facility_id = state.fc_id;
  const [isLoading, , startLoading, stopLoading] = useLoadingIndicator();
  const [data, setData] = useState<FCStaffForListSchema[]>([]);
  const [rankForSelect, setRankForSelect] = useState<GeneralForListSchema[]>([]);
  const moveByClick = (e: React.MouseEvent, routeData: any) => pushWithModifier(e, history, routeData);

  const getSelectValue = (val: number, selectList: GeneralForListSchema[]) => {
    if (val == 0) return;
    return selectList.filter((x) => x.value == val)[0];
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      startLoading();
      try {
        const res = await fetchGeneralsForList(authState, "役職");
        if (isMounted) {
          const forSelect: GeneralForListSchema[] = [];
          res.data.map((x: GeneralForListSchema, i) => {
            forSelect.push({
              value: x.value,
              label: x.label,
            });
          });
          setRankForSelect(forSelect);
        }
      } catch (err) {
        if (isMounted) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (isMounted) {
          stopLoading();
        }
      }
    };
    if (rankForSelect.length == 0) {
      fetchData();
    }
    return () => {
      isMounted = false;
    }
  }, [authState, checkLoggedIn, rankForSelect.length, startLoading, stopLoading]);

  useEffect(() => {
    if (!facility_id) {
      notifyError("施設が選択されていません。");
      history.replace(getForHistoryPush(F001Url, [0]));
    }
  }, [facility_id, history]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!facility_id) return;
      startLoading();
      try {
        const res = await fetchStaffById(authState, facility_id);
        if (isMounted) {
          setData(res.data);
        }
      } catch (err) {
        if (isMounted) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (isMounted) {
          stopLoading();
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    }
  }, [facility_id, authState, checkLoggedIn, startLoading, stopLoading]);

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <Title title="スタッフ一覧" />
      {userInfo?.user_type == HEADQUARTER_USER && (
        <FacilityName fcId={facility_id} authState={authState} />
      )}
      <div className="plate" >
        <div style={{ maxWidth: maxWidth }}>
          <CustomButton
            label="＋スタッフを追加"
            class={[CB_CLASS.RED]}
            align={CB_ALIGN.RIGHT}
            onClick={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(F004Url, [0]))}
          />
        </div>
      </div>
      <PaginationTable
        limit={13}
        max_width={maxWidth}
        headers={["アイコン", "スタッフ名", "役職"]}
        list={data.map((x, i) => (
          <tr key={i} onClick={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(F004Url, [x.id]))}>
            <td className="item" valign="middle" align="center">
              <Pict size={SIZES.M} url={x.profile_pic_url} />
            </td>
            <td className="item">{x.family_name + x.given_name}</td>
            <td className="item" valign="middle" align="center">
              <Label value={getSelectValue(x.rank, rankForSelect)?.label} />
            </td>
          </tr>
        ))}
      />
    </>
  );
};

export default F003;
